/**
 * Company footer — the legal/company block operators must show. It is filled
 * from the PUBLIC endpoint (`GET /api/public/company`) so it works on
 * unauthenticated screens too, and it renders nothing at all when the endpoint
 * is unavailable (never a broken or half-empty footer).
 */
import { el, clear } from '../dom';
import { publicApi, type CompanyInfo } from '../game-api';
import { fetchSiteInfo } from '../../core/preview/preview';

let cache: Promise<CompanyInfo> | null = null;

/** Cached so several footers on one page cause a single request. */
export function fetchCompany(): Promise<CompanyInfo> {
  if (!cache) {
    cache = publicApi.company().catch((e) => { cache = null; throw e; });
  }
  return cache;
}

/** Only in tests — drops the memoised company promise. */
export function resetCompanyCache() { cache = null; }

/** The lines shown in the footer, in order, skipping anything the API omits. */
export function companyLines(c: CompanyInfo | null | undefined): string[] {
  if (!c) return [];
  const lines: string[] = [];
  const legal = [c.legalName || c.name, c.regNumber ? `Reg. ${c.regNumber}` : '', c.vatNumber ? `VAT ${c.vatNumber}` : '']
    .filter(Boolean).join(' · ');
  if (legal) lines.push(legal);
  const where = [c.address, c.country].filter(Boolean).join(', ');
  if (where) lines.push(where);
  const contact = [c.email, c.phone, c.website].filter(Boolean).join(' · ');
  if (contact) lines.push(contact);
  return lines;
}

/**
 * Fills `host` with the company footer. Returns a teardown that cancels the
 * pending fetch so a torn-down view never writes into a detached node.
 */
export function mountCompanyFooter(host: HTMLElement): () => void {
  let cancelled = false;

  Promise.all([
    fetchCompany().catch(() => null),
    fetchSiteInfo().then((si) => si.paymentMethods ?? []).catch(() => []),
  ]).then(([c, paymentMethods]) => {
    if (cancelled) return;
    const lines = companyLines(c);
    if (!lines.length && !paymentMethods.length) return;
    clear(host);

    const children: HTMLElement[] = [];

    if (paymentMethods.length) {
      const badges = paymentMethods.map((m) => {
        const img = document.createElement('img');
        img.src = `/payment-methods/${m.icon}.svg`;
        img.alt = m.name;
        img.title = m.name;
        img.className = 'h-5 w-auto object-contain';
        const badge = el('div',
          'flex items-center justify-center h-8 px-2.5 bg-surface/60 border border-border/40 rounded',
          {}, img);
        return badge;
      });
      children.push(el('div', 'flex flex-wrap items-center justify-center gap-2 mb-2', {}, ...badges));
    }

    if (lines.length) {
      children.push(...lines.map((l) => el('div', 'break-words', {}, l)));
    }

    host.appendChild(el('footer',
      'game-font border-t border-border/60 pt-3 mt-2 text-[11px] text-text-dim leading-relaxed text-center',
      {},
      ...children));
  });

  return () => { cancelled = true; };
}
